<!--
  FilterToolbar Component
  Toolbar above a table with a "Filter" button, active filters as chips, and a "Clear all" link.
  Supports filter types: text, date-range, number-range, enum
-->
<script lang="ts" context="module">
  export interface FilterDef {
    key: string;
    label: string;
    type: 'text' | 'date-range' | 'number-range' | 'enum';
    options?: string[];
    optionLabels?: string[];
    /** Optional document counts per option, shown next to the label in the option list only. */
    optionCounts?: number[];
  }

  /** Union of all concrete filter values produced by FilterToolbar. */
  export type FilterValue =
    | string
    | string[]
    | { from?: string | null; to?: string | null }
    | { min?: number | null; max?: number | null }
    | null;
</script>

<script lang="ts">
  import { createEventDispatcher, tick } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { Filter, X, Plus, Check } from 'lucide-svelte';
  import { userSettings } from '../../stores/settingsStore';
  import { formatDate } from '../../settings';
  import DateInput from './DateInput.svelte';

  export let availableFilters: FilterDef[] = [];
  export let activeFilters: Record<string, FilterValue> = {};
  /** E2E hooks of the bar and its "add filter" button (overridable per page). */
  export let barTestId = 'filter-toolbar';
  export let addTestId = 'filter-toolbar-add';
  /** Chips that stay put on "clear all" and do not count towards showing it (e.g. a scope setting). */
  export let persistentKeys: string[] = [];
  /** Active filters that count (badge, "clear all") but are rendered by the host through the slot. */
  export let hiddenChipKeys: string[] = [];

  const dispatch = createEventDispatcher<{
    filterChange: { key: string; value: FilterValue };
    filterRemove: { key: string };
    clearAll: void;
  }>();

  // UI state
  let dropdownOpen = false;
  let editingFilter: string | null = null;

  // Inline edit state
  let inlineInputEl: HTMLInputElement | undefined;
  let textValue = '';
  let dateFrom = ''; // ISO (YYYY-MM-DD)
  let dateTo = ''; // ISO (YYYY-MM-DD)
  let numberMin = '';
  let numberMax = '';
  let selectedOptions: Set<string> = new Set();

  $: activeKeys = Object.keys(activeFilters).filter((k) => {
    const v = activeFilters[k];
    return v != null && v !== '' && !(Array.isArray(v) && v.length === 0);
  });

  $: activeCount = activeKeys.length;

  $: unusedFilters = availableFilters.filter((f) => !activeKeys.includes(f.key));

  function toggleDropdown() {
    dropdownOpen = !dropdownOpen;
    editingFilter = null;
  }

  function closeDropdown() {
    dropdownOpen = false;
    editingFilter = null;
  }

  function selectFilterFromDropdown(filterDef: FilterDef) {
    editingFilter = filterDef.key;
    // Initialize values from current state or empty
    const current = activeFilters[filterDef.key];
    if (filterDef.type === 'text') {
      textValue = typeof current === 'string' ? current : '';
    } else if (filterDef.type === 'date-range') {
      const dr = current as { from?: string | null; to?: string | null } | null | undefined;
      dateFrom = dr?.from || '';
      dateTo = dr?.to || '';
    } else if (filterDef.type === 'number-range') {
      const nr = current as { min?: number | null; max?: number | null } | null | undefined;
      numberMin = nr?.min != null ? String(nr.min) : '';
      numberMax = nr?.max != null ? String(nr.max) : '';
    } else if (filterDef.type === 'enum') {
      const arr = Array.isArray(current)
        ? (current as string[])
        : current
          ? [current as string]
          : [];
      selectedOptions = new Set(arr);
    }
    tick().then(() => {
      inlineInputEl?.focus();
    });
  }

  function getFilterDef(key: string): FilterDef | undefined {
    return availableFilters.find((f) => f.key === key);
  }

  function applyInlineFilter() {
    if (!editingFilter) return;
    const def = getFilterDef(editingFilter);
    if (!def) return;

    let value: FilterValue = null;
    if (def.type === 'text') {
      value = textValue.trim() || null;
    } else if (def.type === 'date-range') {
      value = dateFrom || dateTo ? { from: dateFrom || null, to: dateTo || null } : null;
    } else if (def.type === 'number-range') {
      const min = numberMin !== '' ? Number(numberMin) : null;
      const max = numberMax !== '' ? Number(numberMax) : null;
      value = min != null || max != null ? { min, max } : null;
    } else if (def.type === 'enum') {
      value = selectedOptions.size > 0 ? [...selectedOptions] : null;
    }

    dispatch('filterChange', { key: editingFilter, value });
    editingFilter = null;
    dropdownOpen = false;
  }

  function handleInlineKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      applyInlineFilter();
    } else if (e.key === 'Escape') {
      editingFilter = null;
      dropdownOpen = false;
    }
  }

  function toggleOption(option: string) {
    if (selectedOptions.has(option)) {
      selectedOptions.delete(option);
    } else {
      selectedOptions.add(option);
    }
    selectedOptions = new Set(selectedOptions);
  }

  function removeFilter(key: string) {
    dispatch('filterRemove', { key });
  }

  function clearAll() {
    dispatch('clearAll');
  }

  // --- Date quick-select helpers ---

  function toIsoDate(d: Date): string {
    return d.toISOString().slice(0, 10);
  }

  function applyDatePreset(from: string, to: string) {
    dateFrom = from;
    dateTo = to;
    applyInlineFilter();
  }

  function selectToday() {
    const today = toIsoDate(new Date());
    applyDatePreset(today, today);
  }

  function selectYesterday() {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const yesterday = toIsoDate(d);
    applyDatePreset(yesterday, yesterday);
  }

  function selectLast7Days() {
    const to = toIsoDate(new Date());
    const d = new Date();
    d.setDate(d.getDate() - 6);
    applyDatePreset(toIsoDate(d), to);
  }

  function selectLast30Days() {
    const to = toIsoDate(new Date());
    const d = new Date();
    d.setDate(d.getDate() - 29);
    applyDatePreset(toIsoDate(d), to);
  }

  // --- Chip formatting ---

  function formatDateForChip(isoDate: string): string {
    return formatDate(isoDate, $userSettings);
  }

  function formatChipValue(key: string, value: FilterValue): string {
    const def = getFilterDef(key);
    if (!def) return String(value);

    if (def.type === 'text') {
      return String(value);
    } else if (def.type === 'date-range') {
      const dr = value as { from?: string | null; to?: string | null } | null | undefined;
      const from = dr?.from ? formatDateForChip(dr.from) : '';
      const to = dr?.to ? formatDateForChip(dr.to) : '';
      if (from && to && from === to) return from;
      if (from && to) return `${from} – ${to}`;
      if (from) return `${$_('table.filter.from')} ${from}`;
      if (to) return `${$_('table.filter.to')} ${to}`;
      return String(value);
    } else if (def.type === 'number-range') {
      const nr = value as { min?: number | null; max?: number | null } | null | undefined;
      const parts: string[] = [];
      if (nr?.min != null) parts.push(`${nr.min}`);
      parts.push('–');
      if (nr?.max != null) parts.push(`${nr.max}`);
      return parts.join(' ');
    } else if (def.type === 'enum') {
      const vals = (Array.isArray(value) ? value : [value]) as string[];
      return vals
        .map((v: string) => {
          if (def.options && def.optionLabels) {
            const idx = def.options.indexOf(v);
            return idx >= 0 ? def.optionLabels[idx] : v;
          }
          return v;
        })
        .join(', ');
    }
    return String(value);
  }

  function getFilterLabel(key: string): string {
    return getFilterDef(key)?.label || key;
  }

  function handleClickOutside(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (!target.closest('.filter-toolbar-dropdown')) {
      closeDropdown();
    }
  }
</script>

<svelte:window on:click={handleClickOutside} />

{#if availableFilters.length > 0}
  <div
    data-testid={barTestId}
    class="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-border"
  >
    <!-- Filter button -->
    <div class="relative filter-toolbar-dropdown">
      <button
        type="button"
        data-testid={addTestId}
        class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border transition-colors cursor-pointer
          {activeCount > 0
          ? 'border-accent/40 bg-accent/5 text-accent hover:bg-accent/10'
          : 'border-border bg-transparent text-text-muted hover:text-text hover:bg-surface-hover'}"
        on:click|stopPropagation={toggleDropdown}
      >
        <Filter class="w-3.5 h-3.5" strokeWidth={1.5} />
        {$_('table.filter.title')}
        {#if activeCount > 0}
          <span
            class="flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-accent text-on-accent rounded-full"
            >{activeCount}</span
          >
        {/if}
      </button>

      <!-- Dropdown -->
      {#if dropdownOpen}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
          data-esc-owner
          class="absolute left-0 top-full mt-1 z-50 bg-surface border border-border rounded-lg shadow-lg min-w-[240px]"
          on:click|stopPropagation
        >
          {#if editingFilter}
            <!-- Inline edit mode -->
            {@const def = getFilterDef(editingFilter)}
            {#if def}
              <div class="p-3">
                <div class="text-xs font-semibold text-text-muted uppercase mb-2">{def.label}</div>

                {#if def.type === 'text'}
                  <input
                    type="text"
                    bind:this={inlineInputEl}
                    bind:value={textValue}
                    on:keydown={handleInlineKeydown}
                    placeholder={$_('table.filter.search')}
                    class="w-full px-2.5 py-1.5 bg-bg-alt border border-border rounded text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                  />
                {:else if def.type === 'date-range'}
                  <!-- Quick-select buttons -->
                  <div class="flex flex-wrap gap-1 mb-2">
                    <button
                      type="button"
                      class="px-2 py-0.5 text-[11px] rounded border border-border text-text-muted hover:text-text hover:bg-surface-hover transition-colors cursor-pointer bg-transparent"
                      on:click={selectToday}>{$_('table.filter.today')}</button
                    >
                    <button
                      type="button"
                      class="px-2 py-0.5 text-[11px] rounded border border-border text-text-muted hover:text-text hover:bg-surface-hover transition-colors cursor-pointer bg-transparent"
                      on:click={selectYesterday}>{$_('table.filter.yesterday')}</button
                    >
                    <button
                      type="button"
                      class="px-2 py-0.5 text-[11px] rounded border border-border text-text-muted hover:text-text hover:bg-surface-hover transition-colors cursor-pointer bg-transparent"
                      on:click={selectLast7Days}>{$_('table.filter.last7days')}</button
                    >
                    <button
                      type="button"
                      class="px-2 py-0.5 text-[11px] rounded border border-border text-text-muted hover:text-text hover:bg-surface-hover transition-colors cursor-pointer bg-transparent"
                      on:click={selectLast30Days}>{$_('table.filter.last30days')}</button
                    >
                  </div>
                  <div class="flex flex-col gap-2">
                    <div class="text-xs text-text-muted">
                      {$_('table.filter.from')}
                      <DateInput
                        value={dateFrom}
                        on:change={(e) => {
                          dateFrom = e.detail;
                        }}
                        on:keydown={handleInlineKeydown}
                      />
                    </div>
                    <div class="text-xs text-text-muted">
                      {$_('table.filter.to')}
                      <DateInput
                        value={dateTo}
                        on:change={(e) => {
                          dateTo = e.detail;
                        }}
                        on:keydown={handleInlineKeydown}
                      />
                    </div>
                  </div>
                {:else if def.type === 'number-range'}
                  <div class="flex gap-2">
                    <label class="flex-1 text-xs text-text-muted">
                      {$_('table.filter.min')}
                      <input
                        type="number"
                        bind:this={inlineInputEl}
                        bind:value={numberMin}
                        on:keydown={handleInlineKeydown}
                        class="w-full px-2.5 py-1.5 bg-bg-alt border border-border rounded text-sm text-text focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                      />
                    </label>
                    <label class="flex-1 text-xs text-text-muted">
                      {$_('table.filter.max')}
                      <input
                        type="number"
                        bind:value={numberMax}
                        on:keydown={handleInlineKeydown}
                        class="w-full px-2.5 py-1.5 bg-bg-alt border border-border rounded text-sm text-text focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                      />
                    </label>
                  </div>
                {:else if def.type === 'enum'}
                  <div class="flex flex-col gap-0.5 max-h-48 overflow-y-auto">
                    {#each def.options || [] as option, i (option)}
                      <button
                        type="button"
                        data-testid="filter-option-{option}"
                        class="flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left transition-colors cursor-pointer border-none bg-transparent
                          {selectedOptions.has(option)
                          ? 'bg-accent/10 text-accent'
                          : 'text-text hover:bg-surface-hover'}"
                        on:click={() => toggleOption(option)}
                      >
                        <span class="w-4 h-4 flex items-center justify-center">
                          {#if selectedOptions.has(option)}
                            <Check class="w-3.5 h-3.5" strokeWidth={2} />
                          {/if}
                        </span>
                        {def.optionLabels?.[i] || option}
                        {#if def.optionCounts?.[i] != null}
                          <span class="ml-auto pl-3 text-xs text-text-muted"
                            >{def.optionCounts[i]}</span
                          >
                        {/if}
                      </button>
                    {/each}
                  </div>
                {/if}

                <div class="flex justify-end mt-3 pt-2 border-t border-border">
                  <button
                    type="button"
                    class="px-3 py-1 text-xs font-medium bg-accent text-on-accent rounded hover:bg-accent-hover cursor-pointer border-none transition-colors"
                    on:click={applyInlineFilter}
                  >
                    {$_('table.filter.apply')}
                  </button>
                </div>
              </div>
            {/if}
          {:else}
            <!-- Filter list -->
            <div class="py-1">
              {#each unusedFilters as filterDef (filterDef.key)}
                <button
                  type="button"
                  data-testid="filter-add-{filterDef.key}"
                  class="flex items-center gap-2 w-full px-3 py-2 text-sm text-left text-text hover:bg-surface-hover transition-colors cursor-pointer border-none bg-transparent"
                  on:click={() => selectFilterFromDropdown(filterDef)}
                >
                  <Plus class="w-3.5 h-3.5 text-text-muted" strokeWidth={1.5} />
                  {filterDef.label}
                </button>
              {/each}
              {#if unusedFilters.length === 0}
                <div class="px-3 py-2 text-xs text-text-muted">{$_('table.filter.title')}</div>
              {/if}
            </div>
          {/if}
        </div>
      {/if}
    </div>

    <!-- Active filter chips -->
    {#each activeKeys.filter((k) => !hiddenChipKeys.includes(k)) as key (key)}
      <span
        data-testid="filter-chip-{key}"
        class="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 text-xs font-medium rounded-full bg-accent/10 text-accent border border-accent/20"
      >
        <span class="text-text-muted">{getFilterLabel(key)}:</span>
        <span class="max-w-[150px] truncate">{formatChipValue(key, activeFilters[key])}</span>
        <button
          type="button"
          data-testid="filter-chip-remove-{key}"
          aria-label={$_('table.filter.remove', { values: { filter: getFilterLabel(key) } })}
          class="ml-0.5 p-0.5 rounded-full hover:bg-accent/20 transition-colors cursor-pointer border-none bg-transparent text-accent"
          on:click={() => removeFilter(key)}
        >
          <X class="w-3 h-3" strokeWidth={2} />
        </button>
      </span>
    {/each}

    <slot name="after-chips" />

    <!-- Clear all -->
    {#if activeCount > 1 && activeKeys.some((k) => !persistentKeys.includes(k))}
      <button
        type="button"
        class="text-xs text-text-muted hover:text-error transition-colors cursor-pointer border-none bg-transparent px-1"
        on:click={clearAll}
      >
        {$_('table.filter.clearAll', { default: 'Clear all' })}
      </button>
    {/if}
  </div>
{/if}
